import { inject, Injectable } from '@angular/core';

import { catchError, exhaustMap, filter, from, map, of, tap } from 'rxjs';

import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';

import { RegistrationQueryParams } from '@app/auth/models/RegistrationParams';
import { GoogleLoginService } from '@app/auth/services/google-login.service';
import { MicrosoftLoginService } from '@app/auth/services/microsoft-login.service';
import { OktaLoginService } from '@app/auth/services/okta-login.service';
import { loginActions } from '@app/auth/store/actions/login.actions';
import { ssoLoginActions } from '@app/auth/store/actions/sso-login.actions';
import { MOBILE_APP_REDIRECT_PARAMS } from '@app/sloneek/models/mobile-app-redirect-params';
import { navigationActions } from '@app/store/actions/navigation.actions';
import { notificationActions } from '@app/store/actions/notification.actions';

const LOGIN_ROUTE = ['/', 'auth', 'login'];

const loginFailedActions = (title: string, message: string): Action[] => [
  notificationActions.error({ title, message, translate: true }),
  navigationActions.navigateTo({ commands: LOGIN_ROUTE }),
];

const decodeRegistrationState = (state: string | undefined): Partial<RegistrationQueryParams> => {
  try {
    return state ? (JSON.parse(atob(state)) as Partial<RegistrationQueryParams>) : {};
  } catch {
    return {};
  }
};

@Injectable()
export class SsoLoginEffects {
  private readonly actions$ = inject(Actions);
  private readonly googleLoginService = inject(GoogleLoginService);
  private readonly microsoftLoginService = inject(MicrosoftLoginService);
  private readonly oktaLoginService = inject(OktaLoginService);

  public readonly googleCallback$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ssoLoginActions.googleCallback),
      exhaustMap(({ code, scope, hd, state }) => {
        if (!code) {
          return from(loginFailedActions(marker('fe.auth.google.failed.login.title'), marker('fe.auth.google.failed.login.message')));
        }

        const { redirectToMobileApp } = decodeRegistrationState(state);

        return from(this.googleLoginService.authenticate(code, scope, hd ?? null)).pipe(
          tap(() => this.googleLoginService.postMessageToParentWindowAfterSuccessLogin()),
          exhaustMap(response =>
            from([
              loginActions.success({ response, skipNavigation: true }),
              redirectToMobileApp
                ? navigationActions.navigateTo({
                    commands: ['/', 'app', 'mobile-app', MOBILE_APP_REDIRECT_PARAMS.LOGIN_USER],
                    extras: { queryParams: { code, scope, hd } },
                  })
                : navigationActions.navigateTo({ commands: ['/', 'app'] }),
            ]),
          ),
          catchError(() => {
            this.googleLoginService.postMessageToParentWindowAfterErrorsLogin();
            return from(
              loginFailedActions(marker('fe.auth.google.failed.registration.title'), marker('fe.auth.google.failed.registration.message')),
            );
          }),
        );
      }),
    ),
  );

  public readonly microsoftCallback$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ssoLoginActions.microsoftCallback),
      exhaustMap(({ code }) => {
        if (!code) {
          return from(loginFailedActions(marker('fe.auth.google.failed.login.title'), marker('fe.auth.google.failed.login.message')));
        }

        return from(this.microsoftLoginService.authenticate(code)).pipe(
          tap(() => this.microsoftLoginService.postMessageToParentWindowAfterSuccessLogin()),
          map(response => loginActions.success({ response })),
          catchError(() => {
            this.microsoftLoginService.postMessageToParentWindowAfterErrorsLogin();
            return from(
              loginFailedActions(marker('fe.auth.google.failed.registration.title'), marker('fe.auth.google.failed.registration.message')),
            );
          }),
        );
      }),
    ),
  );

  public readonly oktaCallback$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ssoLoginActions.oktaCallback),
      exhaustMap(({ code, state }) => {
        if (!code) {
          return from(loginFailedActions(marker('fe.auth.google.failed.login.title'), marker('fe.auth.google.failed.login.message')));
        }

        return this.oktaLoginService.authenticate(code, state).pipe(
          filter(response => !!response),
          map(response => loginActions.success({ response })),
          catchError(() => of(navigationActions.navigateTo({ commands: LOGIN_ROUTE }))),
        );
      }),
    ),
  );
}
